import { Button } from "@/components/ui/button";
import { Copy, Pencil, File, ChevronDown } from "lucide-react";
import { HasAccess } from "@/components/common/has-access";
import { UserProfile } from "@/pages/users/types/user";
import { useToast } from "@/hooks/use-toast";
import { format } from "date-fns";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { UserTagControls, UserTagsSection } from "./user-tags-section";

interface UserProfileHeaderRedesignedProps {
  user: UserProfile;
  onEditClick: () => void;
  // Swaps the drawer to the partner's profile in place (set by the drawer).
  onPartnerClick?: (partnerId: string) => void;
}

const HRC_STATUS_LABELS: Record<string, string> = {
  NOT_INITIATED: "HRC Not Initiated",
  PENDING: "HRC Pending",
  APPROVED: "HRC Approved",
  REJECTED: "HRC Rejected",
};

const MC_STATUS_LABELS: Record<string, string> = {
  NOT_INITIATED: "MC Not Initiated",
  PENDING: "MC Pending",
  APPROVED: "MC Approved",
  REJECTED: "MC Rejected",
};
// One sizing contract for every header capsule: fixed h-6 height with
// items-center keeps the label vertically centered regardless of whether the
// capsule carries an icon, so all pills sit on the same line at the same size.
const CAPSULE_CLASS =
  "inline-flex h-6 items-center gap-1 rounded-full px-3 text-xs font-medium leading-none whitespace-nowrap";

const CONSENT_FORM_STATUS_STYLES: Record<string, string> = {
  NOT_INITIATED: "bg-gray-100 text-gray-800",
  PENDING: "bg-orange-100 text-orange-800",
  APPROVED: "bg-green-100 text-green-800",
  REJECTED: "bg-red-100 text-red-800",
};

export function UserProfileHeaderRedesigned({
  user,
  onEditClick,
  onPartnerClick,
}: UserProfileHeaderRedesignedProps) {
  const { toast } = useToast();

  // Couples-pack context: partner = co-user on a shared usage; pending partner
  // and payer attribution are server-derived (purchaser_id / payment notes).
  const couplesUsage = user.bundles_usage?.find(
    (usage) =>
      (usage.users?.length ?? 0) >= 2 ||
      usage.pending_partner ||
      usage.resolved_purchaser,
  );
  const partner = user.bundles_usage
    ?.flatMap((usage) => usage.users ?? [])
    .find((coUser) => coUser.id !== user.id);
  const pendingPartner = partner
    ? null
    : user.bundles_usage?.find((usage) => usage.pending_partner)
        ?.pending_partner;
  const resolvedPurchaser = couplesUsage?.resolved_purchaser;
  const paidByName = resolvedPurchaser
    ? resolvedPurchaser.user_id === user.id
      ? user.full_name
      : partner?.id === resolvedPurchaser.user_id
      ? partner.full_name
      : null
    : null;

  const copyToClipboard = (text: string, label: string) => {
    navigator.clipboard.writeText(text);
    toast({
      title: "Copied",
      description: `${label} copied to clipboard`,
    });
  };

  const formatLanguages = (lang: string | string[] | null) => {
    // An empty array is truthy, so joining it renders a blank field rather
    // than the "—" placeholder every other row uses.
    const joined = Array.isArray(lang) ? lang.filter(Boolean).join(", ") : lang;
    return joined?.trim() ? joined : "—";
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const day = date.getDate();
    const suffix =
      day === 1 || day === 21 || day === 31
        ? "st"
        : day === 2 || day === 22
        ? "nd"
        : day === 3 || day === 23
        ? "rd"
        : "th";
    return `${day}${suffix} ${format(date, "MMMM yyyy")}`;
  };

  return (
    <div className="space-y-4">
      <div className="flex items-start justify-between">
        <div className="space-y-2 flex-1">
          <div className="flex items-center gap-2">
            <h1 className="text-2xl font-semibold text-gray-900">
              {user.full_name || "User Name"}
              {user.full_name && (
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-6 w-6 text-gray-600 hover:bg-gray-100"
                  onClick={() => copyToClipboard(user.full_name || "", "User Name")}
                >
                  <Copy className="h-3.5 w-3.5" />
                </Button>
              )}
            </h1>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {user.identifiers && user.identifiers.length > 0 && (
              <span className={`${CAPSULE_CLASS} bg-blue-100 text-blue-800`}>
                Authorised
              </span>
            )}
            {user.hrc_status && (
              <span
                className={`${CAPSULE_CLASS} ${
                  CONSENT_FORM_STATUS_STYLES[user.hrc_status]
                }`}
              >
                {HRC_STATUS_LABELS[user.hrc_status]}
              </span>
            )}
            {user.mc_status && (
              <span
                className={`${CAPSULE_CLASS} ${
                  CONSENT_FORM_STATUS_STYLES[user.mc_status]
                }`}
              >
                {MC_STATUS_LABELS[user.mc_status]}
              </span>
            )}
          </div>
          <p className="text-sm text-gray-600">
            Created on {formatDate(user.created_at)}
          </p>
        </div>

        <div className="flex gap-2">
          {user.document_ids && Object.keys(user.document_ids).length > 0 && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="outline"
                  size="sm"
                  className="bg-white border-gray-300 text-gray-700 hover:bg-gray-50"
                >
                  <File className="h-4 w-4 mr-1.5" />
                  Documents
                  <ChevronDown className="h-4 w-4 ml-1.5" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                {Object.entries(user.document_ids).map(([topic, docId]) => (
                  <DropdownMenuItem
                    key={topic}
                    onClick={() => {
                      const docUrl = `https://docs.google.com/document/d/${docId}`;
                      window.open(docUrl, '_blank');
                    }}
                    className="cursor-pointer"
                  >
                    <File className="h-4 w-4 mr-2" />
                    <span className="flex-1 capitalize">{topic}</span>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-6 w-6 ml-2"
                      onClick={(e) => {
                        e.stopPropagation();
                        const docUrl = `https://docs.google.com/document/d/${docId}`;
                        copyToClipboard(docUrl, `${topic} document`);
                      }}
                    >
                      <Copy className="h-3 w-3" />
                    </Button>
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          )}
          <UserTagControls user={user} />
          <HasAccess permission="write">
            <Button
              variant="outline"
              size="sm"
              className="bg-white border-orange-500 text-orange-500 hover:bg-orange-50"
              onClick={onEditClick}
            >
              <Pencil className="h-4 w-4 mr-1.5" />
              Edit Profile
            </Button>
          </HasAccess>
        </div>
      </div>

      <UserTagsSection user={user} />

      <div className="grid grid-cols-2 gap-x-6 gap-y-3 text-sm">
        <div className="flex items-center gap-2">
          <span className="font-semibold text-gray-900">Phone Number:</span>
          <span className="text-gray-700">
            {user.otpless_identifier || user.phone_numbers[0] || "—"}
          </span>
          {user.otpless_identifier || user.phone_numbers[0] ? (
            <Button
              variant="ghost"
              size="icon"
              className="h-5 w-5 text-gray-600 hover:bg-gray-100"
              onClick={() => {
                const phoneNumber =
                  user.otpless_identifier || user.phone_numbers[0] || "";
                const phoneWithoutPlus = phoneNumber.replace(/^\+/, "");
                copyToClipboard(phoneWithoutPlus, "Phone Number");
              }}
            >
              <Copy className="h-3 w-3" />
            </Button>
          ) : null}
        </div>
        <div className="flex items-center gap-2">
          <span className="font-semibold text-gray-900">Email:</span>
          <span className="text-gray-700">{user.emails[0] || "—"}</span>
          {user.emails[0] ? (
            <Button
              variant="ghost"
              size="icon"
              className="h-5 w-5 text-gray-600 hover:bg-gray-100"
              onClick={() => copyToClipboard(user.emails[0] || "", "Email")}
            >
              <Copy className="h-3 w-3" />
            </Button>
          ) : null}
        </div>
        <div>
          <span className="font-semibold text-gray-900">Sex:</span>
          <span className="text-gray-700 ml-2">{user.sex || "—"}</span>
        </div>
        <div>
          <span className="font-semibold text-gray-900">Age:</span>
          <span className="text-gray-700 ml-2">{user.age || "—"}</span>
        </div>
        <div>
          <span className="font-semibold text-gray-900">Country:</span>
          <span className="text-gray-700 ml-2">{user.country || "—"}</span>
        </div>
        <div>
          <span className="font-semibold text-gray-900">Languages:</span>
          <span className="text-gray-700 ml-2">
            {formatLanguages(user.language_preference)}
          </span>
        </div>
        {/* Left column, directly under Country. Interests wrap downward inside
            this cell, growing the row instead of pushing the couples cell out
            of the right column. */}
        <div className="col-start-1 flex flex-wrap items-center gap-1.5 self-start">
          <span className="font-semibold text-gray-900">Interested In:</span>
          {user.product_interests?.length ? (
            user.product_interests.map((interest) => (
              <span
                key={interest}
                className="inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium bg-orange-100 text-orange-800"
              >
                {interest}
              </span>
            ))
          ) : (
            <span className="text-gray-700">—</span>
          )}
        </div>
        {/* Couples context on one line — partner and payer are read together,
            so they share a row separated by a divider. Pinned to the right
            column (col-start-2) so it sits directly under Languages. */}
        {(partner || pendingPartner || paidByName) && (
          <div className="col-start-2 flex flex-wrap items-center gap-x-2 gap-y-1 self-start">
            {(partner || pendingPartner) && (
              <div className="flex items-center gap-2">
                <span className="font-semibold text-gray-900">Partner:</span>
                {partner ? (
                  <button
                    type="button"
                    onClick={() => onPartnerClick?.(partner.id)}
                    className="text-gray-700 underline hover:text-gray-900"
                  >
                    {partner.full_name}
                  </button>
                ) : (
                  <span
                    className="text-gray-700"
                    title={pendingPartner?.phone || pendingPartner?.email || undefined}
                  >
                    {pendingPartner?.full_name || pendingPartner?.phone || pendingPartner?.email}{" "}
                    <span className="text-xs text-gray-500">(not signed up)</span>
                  </span>
                )}
              </div>
            )}
            {(partner || pendingPartner) && paidByName && (
              <span className="text-gray-300">|</span>
            )}
            {paidByName && (
              <div className="flex items-center gap-2">
                <span className="font-semibold text-gray-900">Paid by:</span>
                <span
                  className="text-gray-700"
                  title={
                    resolvedPurchaser?.source === "INFERRED"
                      ? "Inferred from payment notes (pack predates payment attribution)"
                      : "Recorded at payment"
                  }
                >
                  {paidByName}
                  {resolvedPurchaser?.source === "INFERRED" ? " *" : ""}
                </span>
              </div>
            )}
          </div>
        )}
      </div>

      <div className="flex items-center gap-2">
        <span className="text-sm font-semibold text-gray-900">User ID:</span>
        <span className="text-sm text-gray-700 font-mono">{user.id}</span>
        <Button
          variant="ghost"
          size="icon"
          className="h-6 w-6 text-gray-600 hover:bg-gray-100"
          onClick={() => copyToClipboard(user.id, "User ID")}
        >
          <Copy className="h-3.5 w-3.5" />
        </Button>
      </div>
    </div>
  );
}
